/* eslint-disable no-shadow */
/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import { GlobalContext } from 'providers/GlobalProvider';
import CasesAPI from 'api/CasesAPI';
import UsersAPI from 'api/UsersAPI';
import { useSnackbar } from 'notistack';
import { Redirect, useHistory, useParams } from 'react-router-dom';

import DBModuleHeaderLayout from 'components/Common/Layouts/DBModuleHeaderLayout';
import DBProductsModuleHeaderLayout from 'components/Common/Layouts/DBProductsModuleHeaderLayout';
import DatabaseInfo from 'components/BiteDB/Components/DatabaseInfo';
import ProductsInfo from 'components/BiteDB/Components/DatabaseInfoProducts';
import { useGetDatabaseById } from 'api/bitedb/database/database.hooks';
import CircularProgress from '@mui/material/CircularProgress';
import DatabaseNotFound from './DatabaseNotFound';
import { helpContent } from 'components/BiteDB/common/HelpContent';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const Database: React.FC = () => {
  const { cur_user, isWhiteLabel } = useContext(GlobalContext);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams<{ id: string }>();
  const selectedTeamId = cur_user?.selected_team?.id;
  const isValidId = UUID_REGEX.test(id || '');

  const [userHasMonitor, setUserHasMonitor] = useState(true);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(true);
  const [isRouteBlocked, setIsRouteBlocked] = useState(false);
  const [selectedCase, setSelectedCase] = useState<any>({});
  const [ownerEmail, setOwnerEmail] = useState('');
  const [autoID, setAutoID] = useState<number>(0);
  const [cur_case, setCase] = useState<{
    isLoaded: boolean;
    error: string | undefined;
    data: any;
  }>({ isLoaded: false, error: undefined, data: {} });

  // Use the new hook for fetching database by ID
  const {
    data: databaseData,
    isLoading: isDatabaseLoading,
    error: databaseError,
  } = useGetDatabaseById(id, userHasMonitor && isValidId); // Pass userHasMonitor to control when the query runs

  const requiredTiers = ['business', 'enterprise', 'tariff-only'];

  const checkSubscription = async () => {
    if (cur_user.quickcodeOnly || isWhiteLabel) {
      setIsCheckingSubscription(false);
      return;
    }
    try {
      if (
        requiredTiers.some((role) =>
          cur_user.selected_team?.attributes?.tier.includes(role),
        )
      ) {
        return;
      }
      //added check for license
      if (requiredTiers.includes(cur_user.license)) {
        return;
      }
      setUserHasMonitor(false);
    } finally {
      setIsCheckingSubscription(false);
    }
  };

  useEffect(() => {
    checkSubscription();
  }, []);

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  const getOwner = async (id: string) => {
    const res = await UsersAPI.getUser(id);
    if (res?.success) {
      return res?.result.basics.email;
    }
    return '';
  };

  useEffect(() => {
    const fetchOwner = async () => {
      if (!selectedCase || !selectedCase._id) {
        return;
      } // Prevent running when empty

      CasesAPI.getCase(selectedCase._id)?.then(async (res: any) => {
        if (res?.success) {
          if (
            res.result.owner?.email !== cur_user.basics?.email &&
            !res.result.sharedWith.includes(cur_user.basics?.email) &&
            !res.result.sharedWith.includes(cur_user.basics?.id) &&
            !(res.result.createdBy === cur_user.basics?.id)
          ) {
            history.push('/');
          }

          setCase({ isLoaded: true, error: undefined, data: res.result });

          if (res.result.owner) {
            setOwnerEmail(res.result.owner?.email);
          } else {
            const email = await getOwner(res.result.createdBy);
            setOwnerEmail(email);
          }
        } else {
          const err_text = `Error loading case: ${res?.result}`;
          enqueueSnackbar(err_text, { variant: 'error' });
        }
      });
    };

    fetchOwner();
  }, [selectedCase]);

  useEffect(() => {
    if (id) {
      setAutoID(parseInt(id, 10));
    }
  }, [id]);

  // Handle database loading errors
  useEffect(() => {
    if (databaseError) {
      enqueueSnackbar('Failed to fetch database', { variant: 'error' });
    }
  }, [databaseError, enqueueSnackbar]);

  useEffect(() => {
    if (!databaseData || !selectedTeamId) {
      return;
    }
    if (databaseData.group_id !== selectedTeamId) {
      setIsRouteBlocked(true);
    } else {
      setIsRouteBlocked(false);
    }
  }, [databaseData, selectedTeamId]);

  const isTeamMismatch =
    !!databaseData && !!selectedTeamId && databaseData.group_id !== selectedTeamId;

  // A JSX value, not an inner component: an inner component is a new type on
  // every render, which remounts DatabaseInfo (and refires its requests) on
  // every page, sort, tab, search or filter change.
  const dbContent = (
    <>
      {isRouteBlocked || isTeamMismatch ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
          }}
        >
          <div className='hit-title'>
            You do not have access to this database for the Entity Hub. Switch to the correct team to view it.
          </div>
        </div>
      ) : !isValidId ? (
        <DatabaseNotFound />
      ) : isDatabaseLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '450px',
          }}
        >
          <CircularProgress color='primary' />
        </div>
      ) : !id ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
          }}
        >
          <div className='hit-title'>Database not found</div>
        </div>
      ) : databaseData ? (
        <DatabaseInfo
          database={databaseData}
          isLoading={isDatabaseLoading}
        />
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
          }}
        >
          <div className='hit-title'>Database not found</div>
        </div>
      )}
    </>
  );

  return (
    <>
      {isWhiteLabel ? (
        <div style={{
          margin: '0px 50px'
        }}>
          {dbContent}
        </div>
      ) : (
        <DBModuleHeaderLayout
          title={databaseData?.name || 'Database'}
          helpContent={helpContent}
          hubLabel='BITE DB'
          hubTo='/bite-db/entities'
          // The database meta line reads as the title's subtitle, so it sits close under it.
          headerStyle={{ marginBottom: 0 }}
        >
          {dbContent}
        </DBModuleHeaderLayout>
      )}
    </>
  );
};

export default Database;
